import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import {
  verifyPanToCinDetails,
  verifyPanToContact,
  verifyPrefillDetails,
} from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha, isAlphanumeric } from "validator";

function PanToContact() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [pan, setPan] = useState(null);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlphaNumericPan = (value) => {
    if (!value) return;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(setIsJsonCopied(true));
    showSuccessToast("Text Copied");
  };
  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    const validatePan = isAlphaNumericPan(pan);

    if (!validatePan) {
      return setInputError(
        "⚠️ Please enter AlphaNumeric values in Pan No. field",
      );
    } else if (pan.length < 10) {
      return setInputError("⚠️ Enter at least 10 digits in Pan No. field");
    }

    try {
      setIsLoading(true);
      setResponse(null);
      setInputError(null);

      const res = await verifyPanToContact({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        pan: pan.trim(),
      });

      if (res) {
        apiResponse = res;
        setResponse(apiResponse);
        console.log(res);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(response?.message || "Invalid format");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!response.success) {
        showErrorToast(response?.data?.message || "verificaton failed");
      } else if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(res?.status?.message || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center py-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md p-4 sm:p-6 mt-6 bg-white">
          <div className="flex flex-wrap items-center gap-4">
            {/* Mobile */}
            <div className="flex items-center gap-4">
              <label className=" font-bold">
                Pan No: <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={pan}
                maxLength={10}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                className="flex-1 border border-gray-400 uppercase rounded-md px-3 h-[38px]"
                placeholder="Eg: 9187864534"
              />
            </div>

            {/* input errors */}

            {inputError && (
              <div className="text-red-700 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex">
              <button
                disabled={isLoading || !pan}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br active:from-green-600 active:to-green-700 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.data?.status?.code === 500 || response.data?.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-green-50 text-green-700 border border-green-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Info /> Important Instructions !!!
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Pan No. clicking the Verify button. After
              successful verification, the system will return the response in
              structured table and JSON format, including contact details
              associated with the provided details. You may review and copy the
              data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-blue-600 text-white  p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8 w-full border-t border-gray-200" />

              {/* BASIC INFO */}
              <div className="space-y-2 text-sm bg-blue-100 px-4 lg:px-6 py-4 rounded-lg">
                <div>
                  <span className="font-semibold text-[16px] text-blue-500 w-44 inline-block">
                    Full Name:
                  </span>
                  <span className="text-black">
                    {response?.data?.fullname || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-[16px] text-blue-500 w-44 inline-block">
                    Mobile:
                  </span>
                  <span className="text-black">
                    {response?.data?.mobile || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-[16px] text-blue-500 w-44 inline-block">
                    Secondary Mobile:
                  </span>
                  <span className="text-black">
                    {response?.data?.mobile_secondary || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-[16px] text-blue-500 w-44 inline-block">
                    Email:
                  </span>
                  <span className="text-black">
                    {response?.data?.email || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-[16px] text-blue-500 w-44 inline-block">
                    Secondary Email:
                  </span>
                  <span className="text-black">
                    {response?.data?.email_secondary || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PanToContact;
